import { getConfig, type PartnerConfig } from '@roxx-polaris/oem-brand-config';
import { currentEnv, Env, getServiceURL, Service } from '~/module/config/Config';
import { isDefaultTenantScreen, screenTenantID } from '~/module/tenant/tenant';

/**
 * 画面に適用するブランド。
 *
 * どのブランドを用いるかは画面のテナント（ビルド時に決まる）から定まる。画面の実装は
 * ブランド値を直接埋め込まず、必ずここを通す（ブランド分離ガイドライン）。
 */
export type ScreenBrand = {
  /** 既定テナントの画面かどうか。 */
  isDefaultTenant: boolean;
  /**
   * テナントが定まったのにブランドを解決できなかったか（D13）。
   * true でも画面は表示する（操作を妨げない）。呼び出し側は異常として記録する。
   */
  unresolved: boolean;
  /** 露出するサービス名。 */
  serviceName: string;
  /** ロゴ下の文言（「〜にログイン」）。 */
  loginTitle: string;
  /** タブタイトルの接尾（「… | 〜」）。 */
  pageTitleSuffix: string;
  /** ロゴ画像の URL。null は「画面が持つ既定の画像を使う」を意味する。 */
  logo: string | null;
  /** favicon の URL。null は既定（`/favicon.png`）を意味する。 */
  favicon: string | null;
  /** ボタン配色。null は既定のスタイルを意味する。 */
  buttonColor: string | null;
  /** 多要素認証で認証アプリに登録される発行者名。 */
  mfaIssuer: string;
  /** 自テナントの portal のオリジン。空文字は「この画面自身」を意味する。 */
  portalOrigin: string;
  /** AI面接官 管理画面のオリジン。 */
  adminOrigin: string;
};

/**
 * 既定テナント（プラットフォーム自身）のブランド。
 *
 * 提携先の値を配る仕組み（oem-brand-config）にプラットフォーム自身の値を混ぜないため、
 * 既定値は本サービス内に持つ（ブランド分離ガイドライン）。画面の実装が個別に埋め込まず、
 * ここを唯一の入口とする。
 */
const defaultBrand = (): ScreenBrand => ({
  isDefaultTenant: true,
  unresolved: false,
  serviceName: 'Zキャリア',
  loginTitle: 'Zキャリアにログイン',
  pageTitleSuffix: 'Zキャリア',
  logo: null,
  favicon: null,
  buttonColor: null,
  mfaIssuer: 'Zキャリア',
  portalOrigin: '',
  adminOrigin: getServiceURL(Service.aii),
});

const configEnv = (): 'stg' | 'prod' => (currentEnv === Env.production ? 'prod' : 'stg');

const originOf = (host: string | undefined): string => (host ? `https://${host}` : '');

/**
 * ブランド設定に定められたサービス名を返す（定めが無ければ空文字）。
 *
 * **提携先の表示名を消費側に持たない**。サービス名はブランド識別値であり、供給側
 * （oem-brand-config）が定めるものだけを用いる（定めが無い場合は解決失敗として扱う）。
 * 以前は提携先キーごとの表示名を消費側に持っていたが、供給側が明示するようになった
 * ため不要になった。
 */
const resolveServiceName = (config: PartnerConfig): string =>
  config.company?.brand?.serviceName ?? '';

/**
 * 画面のテナントに対応するブランドを返す。
 *
 * 既定テナントは config に定義を持たないのが正常で、既定値を返す。定義があるべき
 * テナント（OEM テナント）で引けなかった場合は、既定値を返しつつ `unresolved` を立てる
 * （画面は出す＝可用性を優先。異常は呼び出し側が記録する。D13）。
 */
export const screenBrand = (): ScreenBrand => {
  if (isDefaultTenantScreen()) {
    return defaultBrand();
  }

  const resolved = getConfig(configEnv(), screenTenantID());
  if (!resolved) {
    return { ...defaultBrand(), isDefaultTenant: false, unresolved: true };
  }

  const company = resolved.config.company;
  const serviceName = resolveServiceName(resolved.config);
  const domain = company?.domain;

  // ブランド識別値（サービス名・ロゴ）は OEM テナントについて必ず定められていなければ
  // ならない。定めが無いまま参照側の既定に落ちると、提携先の画面にプラットフォームの
  // ロゴ・名称が現れる。定めが無いことは解決失敗として扱い、記録できるようにする
  // （画面は出す＝可用性を優先。遷移先の許可は fail-closed になる。D13）。
  if (serviceName === '' || !company?.brand?.logo || !company?.brand?.favicon) {
    return { ...defaultBrand(), isDefaultTenant: false, unresolved: true };
  }

  return {
    isDefaultTenant: false,
    unresolved: false,
    serviceName,
    loginTitle: `${serviceName}にログイン`,
    pageTitleSuffix: serviceName,
    logo: company?.brand?.logo ?? null,
    favicon: company?.brand?.favicon ?? null,
    buttonColor: company?.design?.buttonColor ?? null,
    mfaIssuer: serviceName,
    portalOrigin: originOf(domain?.portalHost),
    adminOrigin: originOf(domain?.adminHost),
  };
};
